<template>
  <span>
    <q-card
      v-ripple="!disable"
      data-e2e="card-frequency-timer"
      class="col-auto column full-height noselect frequency-card"
      :class="{
        'cursor-pointer': !disable,
        'bg-primary-light': !disable,
        'bg-grey-2': disable,
      }"
      @click="onSwitch"
    >
      <q-card-section
        class="col row items-center justify-center"
        :class="{
          'q-py-xs': tiny,
          'q-py-sm': dense && !tiny,
          'q-pb-none': !dense && !tiny,
        }"
      >
        <span
          v-if="tiny && paused"
        >
          {{ pauseMessage }}
        </span>
        <span v-else>
          {{ totalDurationFormatted }}
        </span>
        <template v-if="!disable && !$q.screen.lt.sm">
          <q-icon
            v-if="!isTimerStarted"
            name="play_arrow"
            color="primary"
            size="sm"
            class="q-ml-xs"
          />
          <q-icon
            v-else
            name="pause"
            color="primary"
            size="sm"
            class="q-ml-xs"
          />
        </template>
      </q-card-section>
      <q-card-section
        v-if="!dense"
        class="q-pt-xs"
      >
        <q-chip
          v-if="!dense"
          size="sm"
          text-color="white"
          color="primary"
          icon="timer"
        >
          {{ $t('programs.dataCollection.frequencyTimer.textTitle') }}
        </q-chip>
        <span
          v-else
          :class="{ 'text-subtitle2': !tiny }"
        >{{ totalDurationFormatted }}</span>
      </q-card-section>
    </q-card>
  </span>
</template>
<script>
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';

export default {
  name: 'FrequencyTimer',

  mixins: [Targets, Toolbox],

  props: {
    modelValue: {
      type: Number,
      default: 0,
    },
    paused: {
      type: Boolean,
      default: null,
    },
    durationOffset: {
      type: Date,
      default: null,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    tiny: {
      type: Boolean,
      default: false,
    },
    includeHours: {
      type: Boolean,
      default: false,
    },
    pauseMessage: {
      type: String,
      default: null,
    },
    sessionDurationOffset: {
      type: Date,
      default: null,
    },
    sessionPaused: {
      type: Boolean,
      default: false,
    },

  },

  emits: ['update', 'start', 'pause'],

  data() {
    return {
      timerStart: null,

      currentValue: 0,
      currentDuration: 0,

      timerInterval: null,
    };
  },

  computed: {

    isTimerStarted() {
      return (this.timerInterval !== null);
    }, // isTimerStarted()

    totalDurationFormatted() {
      return this.formatDuration(this.currentValue + this.currentDuration, this.includeHours);
    }, // totalDurationFormatted()
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        this.currentValue = newValue;

        const curTime = new Date();
        const timerOffset = this.durationOffset || new Date();
        this.currentDuration = Math.floor((curTime.getTime() - timerOffset.getTime()) / 1000);
      },
    },

    paused: {
      immediate: true,
      handler(pausedState) {
        if (pausedState) {
          this.stopTimer();
        } else if (!this.isTimerStarted && !this.sessionPaused) {
          const timerOffset = this.durationOffset || new Date();
          this.startTimer(timerOffset);
        }
      },
    },

    sessionPaused: {
      immediate: false,
      handler(sessionPausedState) {
        if (sessionPausedState) {
          this.stopTimer();
          this.$emit('update', this.getTime());
        } else if (!this.paused) {
          const timerOffset = this.sessionDurationOffset || new Date();
          this.$emit('update', this.getTime(), timerOffset);
          this.startTimer(timerOffset);
        }
      },
    },

  }, // watch()

  beforeUnmount() {
    this.stopTimer();
  }, // mounted()

  methods: {
    /**
     * Public: Get current time from timer in seconds
     */
    getTime() {
      return this.currentValue + this.currentDuration;
    }, // getTime()

    /**
     * Called by parent to signal session finish
     */
    finishSession() {
      this.stopTimer();
      return {
        duration: this.getTime(),
      };
    }, // finishSession()

    onSwitch() {
      if (this.disable) {
        return;
      }
      if (!this.isTimerStarted) {
        this.$emit('start', this.getTime());
        return;
      }
      // Pause timer
      this.$emit('pause', this.getTime());
    }, // onSwitch()

    startTimer(timeOffset = null) {
      if (this.isTimerStarted) {
        // Prevent multiple simultanous timers
        return;
      }
      this.timerStart = timeOffset || new Date();
      this.timerInterval = setInterval(() => {
        if (!this.timerStart) {
          return;
        }
        const curTime = new Date();
        this.currentDuration = Math.floor((curTime.getTime() - this.timerStart.getTime()) / 1000);
      }, 50);
    }, // startTimer()

    stopTimer() {
      if (!this.isTimerStarted) {
        return;
      }

      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.timerStart = null;
    },

  },

};
</script>
<style scoped>
.frequency-card {
  min-width:70px;
}

.frequency-card .text-overline {
  font-size: 7px;
  line-height:1rem;
}

</style>
